// Zone type mapping between API format and UI format (P, R, D, Airspace)
export const ZONE_TYPE_MAP = {
  D: "Danger",
  P: "Prohibited",
  R: "Restricted",
  Airspace: "Airspace",
} as const;

export const UI_TYPE_TO_API = {
  Danger: "D",
  Prohibited: "P",
  Restricted: "R",
  Airspace: "Airspace",
} as const;

export type ZoneTypeCode = keyof typeof ZONE_TYPE_MAP;
export type ZoneTypeLabel = keyof typeof UI_TYPE_TO_API;

export const apiTypeToLabel = (type: string): string => {
  return ZONE_TYPE_MAP[type as ZoneTypeCode] || type;
};

export const labelToApiType = (label: string): string => {
  return UI_TYPE_TO_API[label as ZoneTypeLabel] || label;
};
/** Filter label for the Airspace zone type (same behaviour as P/R/D) */
export const AIRSPACE_FILTER_LABEL = "Airspace";

/** Map colours for zone types (NATS: red/fuchsia/orange; Airspace: violet) */
export const ZONE_TYPE_MAP_COLORS: Record<string, string> = {
  Prohibited: "#ef4444",
  Restricted: "#d946ef",
  Danger: "#f97316",
  Airspace: "#8b5cf6",
};

/** Hex colour for map fill/outline by zone type (API type or UI label). */
export const getZoneTypeMapColor = (type: string): string => {
  const label = ZONE_TYPE_MAP[type as ZoneTypeCode] || type;
  return ZONE_TYPE_MAP_COLORS[label] ?? "#94a3b8";
};

export const matchesZoneTypeFilter = (
  zoneType: string,
  filterTypes: string[]
): boolean => {
  if (filterTypes.length === 0) return false;
  const allTypeFilters = ["Prohibited", "Restricted", "Danger", AIRSPACE_FILTER_LABEL];
  if (filterTypes.length === allTypeFilters.length) return true;

  const zoneLabel = apiTypeToLabel(zoneType);
  return filterTypes.includes(zoneLabel);
};

export const matchesAltitudeRange = (
  zoneUpperLimit: number,
  zoneLowerLimit: number,
  minAltitude: number | null,
  maxAltitude: number | null
): boolean => {
  if (minAltitude === null && maxAltitude === null) return true;

  if (minAltitude !== null && maxAltitude === null) {
    return zoneUpperLimit >= minAltitude;
  }

  if (minAltitude === null && maxAltitude !== null) {
    return zoneLowerLimit <= maxAltitude;
  }

  if (minAltitude !== null && maxAltitude !== null) {
    return zoneLowerLimit <= maxAltitude && zoneUpperLimit >= minAltitude;
  }

  return true;
};
